<template>
  <div class="client-time-slot-view row">
    <template v-if="location">
      <div class="col-12">
        <router-link :to="['/online-appointment', locationId, appointmentTypeId].join('/')">
          <i class="fas fa-chevron-left" aria-hidden="true"></i>
          {{ $t('navigation.back') }}
        </router-link>
      </div>

      <div class="col-12 mt-3">
        <div class="text-uppercase h1">{{ localizedDay }}</div>

        <form-input
            type="date"
            text-centered
            class="large-date-control"
            required
            :value="inputFormattedDate"
            @change="startNavTimeout"
        />

        <template v-if="appointmentsFromNow.length > 0">

          <button v-if="appointmentStart" type="button"
                  class="btn-lg btn btn-dark btn-block position-relative px-5"
                  @click="appointmentStart = null; appointmentEnd = null"
          >
            {{ toFormattedTime(appointmentStart.start) }}
            <span v-if="getPossibleEndsForSlotGroup(appointmentStartGroupIdx, appointmentStart).length === 1"
                  class="text-nowrap"
            >
              - {{ toFormattedTime(getPossibleEndsForSlotGroup(appointmentStartGroupIdx, appointmentStart)[0].end) }} {{
                $t('general.oclock')
              }}
            </span>
            <template v-else>{{ $t('general.oclock') }}</template>
            <span class=" position-absolute" style="right: 10px; top: 50%; transform: translateY(-50%)">&times;</span>
          </button>
          <template v-else v-for="(slots, idx) in appointmentsFromNow">
            <button v-for="slot in slots"
                    type="button"
                    class="btn-lg btn btn-light btn-block px-5"
                    @click="setAppointmentStart(idx, slot)"
            >
              {{ toFormattedTime(slot.start) }}
              <template v-if="getPossibleEndsForSlotGroup(idx, slot).length === 1">
                - {{ toFormattedTime(getPossibleEndsForSlotGroup(idx, slot)[0].end) }}
              </template>
              {{ $t('general.oclock') }}
            </button>
            <hr>
          </template>
        </template>
        <div v-else-if="pending"
             class="text-center overflow-hidden mx-5 px-4">
          <img src="../../assets/loading.svg" class="img-fluid " alt="" style="max-height: 4em">
        </div>
        <span v-else class="text-muted font-italic d-inline-block my-4">
          {{ $t('choose_time_slot.choose_another_day') }}
        </span>

        <button type="button"
                :disabled="!appointmentStart"
                class="btn btn-primary btn-block btn-lg my-4 font-weight-light"
                @click="onSubmit"
        >
          {{ $t('choose_time_slot.next') }}
        </button>

      </div>
    </template>
  </div>
</template>

<script>

import FormInput from 'pixelstein-vue-app-package/src/vue2/PsForm/PsFormInput';

import {mapActions, mapState} from 'vuex';
import dayjs from 'dayjs';

export default {
  name: 'ClientAppointments',
  components: {FormInput},
  props: {
    appointmentTypeId: {type: String, default: null},
    locationId: {type: String, default: null},
    date: {type: String, default: null},
  },
  watch: {
    date(nv, ov) {
      if (nv && nv !== ov) {
        this.fetchAvailableTimeSlots();
      }
    },
  },
  data() {
    return {
      groupSize: 1,
      availableTimeSlots: [],
      appointmentStartGroupIdx: 0,
      appointmentStart: null,
      appointmentEnd: null,
      pending: true,
      useDefaultHeader: false,
    };
  },
  computed: {
    ...mapState({
      locations: state => state.Api.Locations.all,
      tokenData: state => state.Api.authTokenData,
      appointmentTypes: state => state.Api.AppointmentTypes.all,
    }),
    selectedAppointmentType() {
      return this.appointmentTypes.find(({id}) => id === this.appointmentTypeId) ?? null;
    },
    duration() {
      return this.selectedAppointmentType?.duration ?? 15;
    },
    location() {
      return this.locations.find(({id}) => id === this.locationId);
    },
    localizedDay() {
      return dayjs(this.date).format('dddd');
    },
    formattedDate() {
      return dayjs(this.date).format('DD.MM.YY');
    },
    inputFormattedDate() {
      return dayjs(this.date).format(this.$config.API_DATE_FORMAT);
    },
    appointmentsFromNow() {
      let now = dayjs().unix();
      let result = [];
      this.availableTimeSlots.forEach(stack => {
        let filtered = stack.filter(el => dayjs(el.end).unix() > now);
        if (filtered.length > 0) {
          result.push(filtered);
        }
      });
      return result;
    },
    possibleAppointmentEnds() {
      return this.getPossibleEndsForSlotGroup(this.appointmentStartGroupIdx, this.appointmentStart);
    },
  },
  mounted() {
    if (!this.selectedAppointmentType) {
      this.$router.replace(['/online-appointment', this.locationId].join('/'));
      return;
    }
    this.fetchAvailableTimeSlots();
  },
  beforeDestroy() {
    if (this.navTimeout) {
      window.clearTimeout(this.navTimeout);
    }
    if (this.submitDebounce) {
      window.clearTimeout(this.submitDebounce);
    }
  },
  methods: {
    ...mapActions({
      getAvailableTimeSlots: 'Api/Locations/available-slots',
    }),
    getPossibleEndsForSlotGroup(groupIdx, forSlot) {
      return this.appointmentsFromNow[groupIdx].filter(slot => dayjs(slot.start).unix() >= dayjs(forSlot.start).unix()).
          filter(slot => dayjs(slot.end).unix() <=
              dayjs(forSlot.start).add(this.location.max_booking_time_minutes, 'minutes').unix());
    },
    resetSelection() {
      this.appointmentStartGroupIdx = 0;
      this.availableTimeSlots = [];
      this.appointmentStart = null;
      this.appointmentEnd = null;
      this.pending = true;
    },
    fetchAvailableTimeSlots() {
      this.resetSelection();

      this.getAvailableTimeSlots({
        date: dayjs(this.date).format(this.$config.API_DATE_TIME_FORMAT),
        id: this.locationId,
        duration: this.duration,
        all_days: false,
      }).
          then(result => this.availableTimeSlots = result).
          catch(this.$apiErrorHandler).
          finally(() => this.pending = false);
    },
    toFormattedTime(dateTime) {
      return dayjs(dateTime).format('HH:mm');
    },
    startNavTimeout(date) {
      this.resetSelection();

      if (this.navTimeout) {
        window.clearTimeout(this.navTimeout);
      }

      this.navTimeout = window.setTimeout(() => {
        let now = dayjs();
        let dateDjs = dayjs(date);
        if (now.isSame(dateDjs, 'day')) {
          dateDjs.hour(now.hour()).minute(now.minute());
        }

        this.$router.replace(dateDjs.format(this.$config.API_DATE_FORMAT));
      }, 500);
    },
    setAppointmentStart(idx, slot) {
      this.appointmentStartGroupIdx = idx;
      this.appointmentStart = slot;

      if (this.getPossibleEndsForSlotGroup(idx, slot).length === 1) {
        this.appointmentEnd = this.getPossibleEndsForSlotGroup(idx, slot)[0];
      }

    },
    onSubmit() {
      this.$router.push([
        '/online-appointment',
        this.locationId,
        this.appointmentTypeId,
        this.appointmentStart?.start,
        'visitor-data'].join('/'));
    },
  },
};
</script>

